import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  abnLabel,
  invoiceQuery,
  invoicesQuery,
  invoiceState,
  issueInvoice,
  PAYMENT_METHODS,
  recordPayment,
  type Invoice,
  type PaymentInput,
  type PaymentMethod,
} from '../api/invoices.ts';
import { bpToPercent } from '../api/agreements.ts';
import {
  applyCredit,
  creditNotesQuery,
  draftCreditNote,
  type CreditNoteSummary,
} from '../api/credit-notes.ts';
import { meQuery } from '../api/me.ts';
import { dayLabel, dayWithYear } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { renderInvoiceDocument } from '../api/documents.ts';
import { DocumentsPanel } from './documents-panel.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// One invoice (CB-REQ FIN-01 and FIN-03 as built at v1.947), laid out as the tax invoice it is: the
// issuer and their ABN, the recipient, the lines ex GST, GST as a stated total. A draft has no number
// until Office issues it; once issued nothing on it changes, and it is paid when nothing is owed
// after payments, credits and applied credit (FIN-03 at v1.993), which is what lets the gear leave
// on a prepay hire (WHS-09). An issued invoice is corrected by a credit (FIN-02).

export function InvoiceScreen() {
  const { invoiceId } = useParams({ from: '/authenticated/invoices/$invoiceId' });
  const invoice = useQuery(invoiceQuery(invoiceId));
  const me = useQuery(meQuery);
  const queryClient = useQueryClient();
  const mayManage = me.data?.capabilities.includes('invoices.manage') ?? false;
  const today = me.data ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toString() : '';

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: invoicesQuery.queryKey });
  };
  const issue = useMutation({
    mutationFn: (dueDate: string | null) => issueInvoice(invoiceId, dueDate),
    onSuccess: refresh,
  });
  const pay = useMutation({
    mutationFn: (input: PaymentInput) => recordPayment(invoiceId, input),
    onSuccess: refresh,
  });
  const navigate = useNavigate();
  const credit = useMutation({
    mutationFn: () => draftCreditNote(invoiceId),
    onSuccess: async (note) => {
      await queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      await refresh();
      await navigate({ to: '/credit-notes/$creditNoteId', params: { creditNoteId: note.id } });
    },
  });
  const held = useQuery({
    ...creditNotesQuery({ clientId: invoice.data?.clientId ?? '', held: true }),
    enabled: !!invoice.data && invoice.data.status === 'issued' && !invoice.data.paid,
  });
  const apply = useMutation({
    mutationFn: ({ id, amountMinor }: { id: string; amountMinor: string }) =>
      applyCredit(id, invoiceId, amountMinor),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      await refresh();
    },
  });

  if (invoice.isPending) return <p className="text-muted">Loading…</p>;
  if (invoice.isError) {
    return (
      <p role="alert" className="alert">
        {invoice.error.message}
      </p>
    );
  }
  const i = invoice.data;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="link self-start text-sm">
        ← Invoices
      </Link>
      <PageHeader
        kicker={`Invoice · ${invoiceState(i, today)}`}
        title={i.reference ?? 'Draft invoice'}
        meta={`${i.clientName} · ${i.job.reference}`}
        actions={
          i.status === 'issued' && !i.creditedInFull && mayManage ? (
            <Button variant="quiet" disabled={credit.isPending} onClick={() => credit.mutate()}>
              Credit…
            </Button>
          ) : null
        }
      />
      {credit.error ? (
        <p role="alert" className="alert">
          {credit.error.message}
        </p>
      ) : null}

      {i.status === 'draft' ? (
        mayManage ? (
          <IssuePanel
            invoice={i}
            today={today}
            pending={issue.isPending}
            error={issue.error}
            onIssue={(dueDate) => issue.mutate(dueDate)}
          />
        ) : (
          <p className="text-ink-soft">
            This invoice is a draft: it has no number until Office issues it.
          </p>
        )
      ) : null}

      <TaxInvoice invoice={i} />

      {i.creditNotes.length > 0 ? (
        <section className="flex flex-col gap-2" aria-labelledby="credits">
          <h2 id="credits" className="section-title">
            Credits
          </h2>
          {i.creditNotes.map((c) => (
            <p key={c.id}>
              <Link
                to="/credit-notes/$creditNoteId"
                params={{ creditNoteId: c.id }}
                className="link"
              >
                {c.reference ?? 'Draft credit'}
              </Link>
              {c.total ? ` · ${formatMinorUnits(c.total.amountMinor, c.total.currency)}` : ''}
            </p>
          ))}
        </section>
      ) : null}

      {i.status === 'issued' && !i.paid && i.owing && mayManage ? (
        <ApplyCredit
          credits={(held.data ?? []).filter((c) => c.invoice.id !== i.id)}
          owing={i.owing}
          pending={apply.isPending}
          error={apply.error}
          onApply={(id, amountMinor) => apply.mutate({ id, amountMinor })}
        />
      ) : null}
      {i.status === 'issued' && !i.paid && i.owing && mayManage ? (
        <PaymentForm
          key={i.owing.amountMinor}
          total={i.owing}
          today={today}
          pending={pay.isPending}
          error={pay.error}
          onSubmit={(input) => pay.mutate(input)}
        />
      ) : null}
      {i.payments.length > 0 || i.creditApplied.length > 0 ? (
        <section className="flex flex-col gap-2" aria-labelledby="payments">
          <h2 id="payments" className="section-title">
            Paid
          </h2>
          {i.creditApplied.map((a) => (
            <p key={`${a.creditNoteId}-${a.appliedAt}`}>
              <span className="font-extrabold">
                {formatMinorUnits(a.amount.amountMinor, a.amount.currency)}
              </span>{' '}
              of credit from{' '}
              <Link
                to="/credit-notes/$creditNoteId"
                params={{ creditNoteId: a.creditNoteId }}
                className="link"
              >
                {a.creditNoteReference}
              </Link>
              .
            </p>
          ))}
          {i.payments.map((p, index) => (
            <p key={index}>
              <span className="font-extrabold">
                {formatMinorUnits(p.amount.amountMinor, p.amount.currency)}
              </span>{' '}
              by {methodLabel(p.method).toLowerCase()}, received{' '}
              {dayLabel(
                Temporal.Instant.from(p.receivedAt)
                  .toZonedDateTimeISO(me.data?.tenant.defaultTz ?? 'UTC')
                  .toPlainDate()
                  .toString(),
              )}
              {p.reference ? ` · ${p.reference}` : ''}.
            </p>
          ))}
          {i.owing && BigInt(i.owing.amountMinor) > 0n ? (
            <p className="text-ink-soft">
              Owing {formatMinorUnits(i.owing.amountMinor, i.owing.currency)}.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* DOC-01 at v1.959: titled "Tax invoice" only once issued; a draft says it is not one. */}
      <DocumentsPanel
        owner="invoice"
        id={i.id}
        actions={[
          {
            label: i.status === 'issued' ? 'Tax invoice PDF' : 'Draft invoice PDF',
            prepare: () => renderInvoiceDocument(i.id),
          },
        ]}
      />
    </div>
  );
}

/** The document itself: what the ATO asks a tax invoice to state, and nothing it does not. */
export function TaxInvoice({ invoice: i }: { invoice: Invoice }) {
  // VEN-11 at v1.989: a resale venue's invoice shows each line as approved and its discount.
  const trade = i.lines.some((l) => l.tradeDiscountBp > 0);
  return (
    <article
      aria-labelledby="document-title"
      className="flex flex-col gap-5 border-2 border-divider p-4 sm:p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 id="document-title" className="text-xl font-extrabold">
            {i.status === 'issued' ? 'Tax invoice' : 'Draft — not yet a tax invoice'}
          </h2>
          <p className="mt-1 font-semibold">{i.issuer.legalName}</p>
          <p className="text-sm text-ink-soft">
            {i.issuer.abn ? `ABN ${abnLabel(i.issuer.abn)}` : 'No ABN recorded yet'}
          </p>
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-soft">Number</dt>
          <dd className="font-semibold">{i.reference ?? 'On issue'}</dd>
          <dt className="text-ink-soft">Issued</dt>
          <dd>{i.issueDate ? dayLabel(i.issueDate) : '—'}</dd>
          <dt className="text-ink-soft">Due</dt>
          <dd>{i.dueDate ? dayLabel(i.dueDate) : '—'}</dd>
          <dt className="text-ink-soft">Job</dt>
          <dd>
            <Link to="/jobs/$jobId" params={{ jobId: i.job.id }} className="link">
              {i.job.reference}
            </Link>
          </dd>
        </dl>
      </header>

      <div>
        <p className="kicker">Bill to</p>
        <p className="font-semibold">{i.clientName}</p>
        {/* VEN-11 at v1.989: a resale venue's invoice names who asked, and for whom. */}
        {requestedBy(i) ? <p className="text-sm text-ink-soft">{requestedBy(i)}</p> : null}
      </div>

      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">What this invoice charges, ex GST</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" className="text-right">
                Qty
              </th>
              {trade ? (
                <>
                  <th scope="col" className="text-right">
                    Approved
                  </th>
                  <th scope="col" className="text-right">
                    Discount
                  </th>
                </>
              ) : null}
              <th scope="col">Tax</th>
              <th scope="col" className="text-right">
                {trade ? 'Net ex GST' : 'Amount ex GST'}
              </th>
            </tr>
          </thead>
          <tbody>
            {i.lines.map((l) => (
              <tr key={l.id}>
                <th scope="row" className="font-semibold">
                  {l.description}
                </th>
                <td className="text-right tabular-nums">{l.quantity}</td>
                {trade ? (
                  <>
                    <td className="text-right tabular-nums">
                      {formatMinorUnits(l.approved.amountMinor, l.approved.currency)}
                    </td>
                    <td className="text-right text-sm tabular-nums">{discountOf(l)}</td>
                  </>
                ) : null}
                <td className="text-sm">{l.taxCode}</td>
                <td className="text-right tabular-nums">
                  {/* VEN-11 at v1.992: a covered line at its price; the allocation comes off below. */}
                  {formatMinorUnits(beforeAllocation(l), l.total.currency)}
                  {l.venueGear && l.approved.amountMinor !== '0' ? (
                    <span className="block text-xs text-muted">Selling service</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="ml-auto grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 border-t-2 border-divider pt-3">
        {i.allocation ? (
          <>
            <dt className="text-ink-soft">
              {`Complimentary allocation (period from ${dayWithYear(i.allocation.periodStart)})`}
            </dt>
            <dd className="text-right tabular-nums">
              −{formatMinorUnits(i.allocation.amount.amountMinor, i.allocation.amount.currency)}
            </dd>
          </>
        ) : null}
        <dt className="text-ink-soft">Subtotal, ex GST</dt>
        <dd className="text-right tabular-nums">
          {formatMinorUnits(i.totals.subtotal.amountMinor, i.totals.subtotal.currency)}
        </dd>
        {i.totals.taxes
          .filter((t) => t.tax.amountMinor !== '0')
          .map((t) => (
            <div key={t.taxCode} className="contents">
              <dt className="text-ink-soft">{t.taxCode}</dt>
              <dd className="text-right tabular-nums">
                {formatMinorUnits(t.tax.amountMinor, t.tax.currency)}
              </dd>
            </div>
          ))}
        <dt className="font-extrabold">Total, including GST</dt>
        <dd className="text-right text-lg font-extrabold tabular-nums">
          {formatMinorUnits(i.totals.total.amountMinor, i.totals.total.currency)}
        </dd>
      </dl>
      {i.allocation ? (
        <p className="ml-auto text-sm text-ink-soft">
          {`${formatMinorUnits(i.allocation.left.amountMinor, i.allocation.left.currency)} of the allocation left in the period.`}
        </p>
      ) : null}
    </article>
  );
}

function IssuePanel({
  invoice,
  today,
  pending,
  error,
  onIssue,
}: {
  invoice: Invoice;
  today: string;
  pending: boolean;
  error: Error | null;
  onIssue: (dueDate: string | null) => void;
}) {
  const [dueDate, setDueDate] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    onIssue(dueDate || null);
  }

  return (
    <form
      onSubmit={submit}
      aria-labelledby="issue"
      className="flex flex-col gap-4 border-2 border-accent p-4"
    >
      <h2 id="issue" className="section-title">
        Check it, then issue it
      </h2>
      <p className="text-sm text-ink-soft">
        {invoice.accountDays === null
          ? 'Drafted from the accepted quote. Issuing gives it the next number and freezes it: after that, a correction is a credit. Every line the client accepted is here, including any still short on the job.'
          : 'Drafted when the last of the gear came back. Issuing gives it the next number and freezes it: after that, a correction is a credit.'}
      </p>
      {invoice.issuer.abn ? null : (
        <p role="alert" className="alert">
          {invoice.issuer.legalName} has no ABN recorded, and a tax invoice must state one. It is
          set when the company is provisioned.
        </p>
      )}
      <div className="max-w-xs">
        <Field
          label="Due date"
          type="date"
          min={today || undefined}
          hint={
            invoice.accountDays === null
              ? 'Leave it blank and it is due by the first hire day, or today if that has passed.'
              : invoice.monthlySettlement
                ? `Leave it blank and it is due ${invoice.accountDays} days after the end of the month it is issued in.`
                : `Leave it blank and it is due ${invoice.accountDays} days from the day it is issued.`
          }
          value={dueDate}
          onChange={(e) => setDueDate(e.currentTarget.value)}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          {pending ? 'Issuing…' : 'Issue'}
        </Button>
      </div>
    </form>
  );
}

export function PaymentForm({
  total,
  today,
  pending,
  error,
  onSubmit,
}: {
  total: Invoice['totals']['total'];
  today: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (input: PaymentInput) => void;
}) {
  const [amount, setAmount] = useState(minorUnitsToDollars(total.amountMinor));
  const [receivedOn, setReceivedOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [problem, setProblem] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    const amountMinor = dollarsToMinorUnits(amount);
    if (!amountMinor) return setProblem('An amount in dollars and cents, like 1299.50.');
    if (method === 'other' && !reference.trim()) {
      return setProblem('Say what the payment was: “other” needs a reference.');
    }
    setProblem(undefined);
    onSubmit({ amountMinor, receivedOn, method, reference: reference.trim() || null });
  }

  return (
    <form
      onSubmit={submit}
      aria-labelledby="record-payment"
      className="flex flex-col gap-4 border-2 border-divider p-4"
    >
      <h2 id="record-payment" className="section-title">
        Record the payment
      </h2>
      <p className="text-sm text-ink-soft">Up to what it owes, once it has arrived.</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Amount received ($)"
          inputMode="decimal"
          required
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
        <Field
          label="Received on"
          type="date"
          required
          max={today || undefined}
          value={receivedOn}
          onChange={(e) => setReceivedOn(e.currentTarget.value)}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Method</span>
          <select
            className="input"
            value={method}
            onChange={(e) => setMethod(e.currentTarget.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <Field
          label={method === 'other' ? 'Reference' : 'Reference (optional)'}
          hint="A bank reference, receipt number, or what it was."
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
      </div>
      {problem || error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? error?.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          {pending ? 'Recording…' : 'Record payment'}
        </Button>
      </div>
    </form>
  );
}

/** FIN-02 at v1.993: the client's credit held on other invoices, applied here by Office. */
export function ApplyCredit({
  credits,
  owing,
  pending,
  error,
  onApply,
}: {
  credits: CreditNoteSummary[];
  owing: NonNullable<Invoice['owing']>;
  pending: boolean;
  error: Error | null;
  onApply: (creditNoteId: string, amountMinor: string) => void;
}) {
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | undefined>();
  if (credits.length === 0) return null;
  const suggested = (c: CreditNoteSummary) =>
    minorUnitsToDollars(
      (BigInt(c.held.amountMinor) < BigInt(owing.amountMinor)
        ? c.held.amountMinor
        : owing.amountMinor
      ).toString(),
    );
  return (
    <section className="flex flex-col gap-3 border-2 border-divider p-4" aria-labelledby="apply">
      <h2 id="apply" className="section-title">
        Credit held
      </h2>
      {credits.map((c) => (
        <form
          key={c.id}
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const amountMinor = dollarsToMinorUnits(amounts[c.id] ?? suggested(c));
            if (!amountMinor) return setProblem('An amount in dollars and cents, like 120.50.');
            setProblem(undefined);
            onApply(c.id, amountMinor);
          }}
        >
          <p className="min-w-40">
            <Link to="/credit-notes/$creditNoteId" params={{ creditNoteId: c.id }} className="link">
              {c.reference}
            </Link>{' '}
            · {formatMinorUnits(c.held.amountMinor, c.held.currency)} held
          </p>
          <Field
            label="Apply ($)"
            inputMode="decimal"
            value={amounts[c.id] ?? suggested(c)}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setAmounts((all) => ({ ...all, [c.id]: value }));
            }}
          />
          <Button type="submit" disabled={pending}>
            Apply
          </Button>
        </form>
      ))}
      {problem || error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem ?? error?.message}
        </p>
      ) : null}
    </section>
  );
}

function methodLabel(method: PaymentMethod): string {
  return PAYMENT_METHODS.find(([m]) => m === method)?.[1] ?? method;
}

type InvoiceLine = Invoice['lines'][number];

/** VEN-11 at v1.989: "Trade discount 15% −$300.00", or for venue gear "Your share 90% −$720.00". */
export function discountOf(l: InvoiceLine): string {
  if (l.tradeDiscountBp === 0) return '';
  const off = (BigInt(l.approved.amountMinor) - BigInt(beforeAllocation(l))).toString();
  return `${l.venueGear ? 'Your share' : 'Trade discount'} ${bpToPercent(l.tradeDiscountBp)}% −${formatMinorUnits(off, l.total.currency)}`;
}

/** Only where the bill-to is not the requester, as on a resale venue's invoice. */
export function requestedBy(i: Invoice): string | null {
  const requester = i.requesterName && i.requesterName !== i.clientName ? i.requesterName : null;
  if (!requester) return null;
  return i.endClientName
    ? `Requested by ${requester}, for ${i.endClientName}`
    : `Requested by ${requester}`;
}

/** VEN-11 at v1.992: a line's net before its part of the venue's allocation came off it. */
export function beforeAllocation(l: InvoiceLine): string {
  return (BigInt(l.total.amountMinor) + BigInt(l.allocation.amountMinor)).toString();
}
